import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import './Sidebar.css';
import { jwtDecode } from 'jwt-decode';
import { normalizeMenuKey } from '../utils/pagePermissions';
import { resolveNavRoute, hasV2Variant, isV2DuplicateRoute } from '../config/menuManifest';
import { useUiVersion } from '../context/UiVersionContext';
import {
  readRecentMenuRoutes,
  writeRecentMenuRoutes,
} from '../utils/recentMenu';

const drawerWidth = 200;
const EMPTY_ROLES = Object.freeze([]);
const normalizeRoleKey = (value = '') => String(value || '').trim().toLowerCase().replace(/[-_\s]/g, '');
const normalizeMenuSearchValue = (value = '') => String(value || '').trim().toLowerCase();

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minHeight: '44px',
  padding: theme.spacing(0, 1),
  justifyContent: 'flex-end',
  borderBottom: '1px solid #e2e8f4',
  background: '#ffffff',
}));

const Sidebar = ({ open, setOpen, isMobile = false, menuAccess = [], authProfile = null }) => {
  const { uiVersion } = useUiVersion();
  const token = localStorage.getItem('token');
  const decodedToken = token ? jwtDecode(token) : {};
  const userPermissions =
    Array.isArray(authProfile?.permissions) && authProfile.permissions.length
      ? authProfile.permissions
      : Array.isArray(decodedToken.permissions)
      ? decodedToken.permissions
      : EMPTY_ROLES;
  const userRoles =
    Array.isArray(authProfile?.roles) && authProfile.roles.length
      ? authProfile.roles
      : Array.isArray(decodedToken.roles)
      ? decodedToken.roles
      : EMPTY_ROLES;
  const normalizedRoleSet = useMemo(
    () => new Set(userRoles.map((role) => normalizeRoleKey(role)).filter(Boolean)),
    [userRoles]
  );
  const hasRole = useCallback(
    (...roles) => roles.some((role) => normalizedRoleSet.has(normalizeRoleKey(role))),
    [normalizedRoleSet]
  );
  const normalizedPermissionSet = useMemo(
    () => new Set(userPermissions.map((permission) => String(permission || '').trim().toLowerCase()).filter(Boolean)),
    [userPermissions]
  );
  const isAdmin = hasRole('admin');
  const isTestAdmin = hasRole('testadmin');
  const canViewAllLogs =
    normalizedPermissionSet.has('log:view') ||
    normalizedPermissionSet.has('can.view.device.logs') ||
    isAdmin ||
    isTestAdmin ||
    hasRole('superAdmin', 'super-admin', 'PMIAdmin');
  const canSeePmiDeviceLogs = canViewAllLogs;
  const canSeeCampaignAndFileLogs = canViewAllLogs;
  const canManageAccess = hasRole('admin', 'superAdmin', 'super-admin');
  const canSeeWeeklyReport = hasRole('admin', 'superAdmin', 'super-admin', 'PMIAdmin', 'TALAdmin');
  const canSeeInactiveEscalation = hasRole('admin', 'superAdmin', 'super-admin', 'PMIAdmin');
  const explicitMenuAccessSet = useMemo(
    () => new Set((Array.isArray(menuAccess) ? menuAccess : []).map((item) => normalizeMenuKey(item)).filter(Boolean)),
    [menuAccess]
  );
  const hasExplicitMenuAccess = explicitMenuAccessSet.size > 0;
  const canAccessMenu = useCallback(
    (menuKey, fallbackVisible = true) => {
      if (!menuKey) return Boolean(fallbackVisible);
      if (!hasExplicitMenuAccess) return Boolean(fallbackVisible);
      return explicitMenuAccessSet.has(normalizeMenuKey(menuKey));
    },
    [explicitMenuAccessSet, hasExplicitMenuAccess]
  );
  const location = useLocation();
  const canSeeAccessControl = canAccessMenu('access control', canManageAccess);
  const canSeeAccessAuditLogs = canAccessMenu('access audit logs', canManageAccess || canViewAllLogs);
  const canSeeRolesPermissions = canAccessMenu('roles & permissions', canManageAccess);
  const canSeeServerHealth = canAccessMenu(
    'server health',
    canManageAccess || normalizedPermissionSet.has('can.view.server.health')
  );
  const canSeeInactiveEscalationMenu = canAccessMenu('inactive escalation', canSeeInactiveEscalation);
  const canSeePmiRuntimeControls = canAccessMenu('pmi runtime controls', false);
  const canSeeVersionManagement = canAccessMenu('version management', canManageAccess);
  const canSeeDeviceConfigs = canAccessMenu('device configs', canManageAccess);
  const isSettingsRoute =
    (location.pathname === '/access-control' && canSeeAccessControl) ||
    (location.pathname === '/access-audit-logs' && canSeeAccessAuditLogs) ||
    (location.pathname === '/roles-permissions-v2' && canSeeRolesPermissions) ||
    (location.pathname === '/server-health' && canSeeServerHealth) ||
    (location.pathname === '/pmi-runtime-controls' && canSeePmiRuntimeControls) ||
    (location.pathname === '/version-management' && canSeeVersionManagement) ||
    (location.pathname === '/device-configs' && canSeeDeviceConfigs);
  const [settingsOpen, setSettingsOpen] = useState(isSettingsRoute);
  const [menuSearch, setMenuSearch] = useState('');

  const theme = useTheme();

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isSettingsRoute) {
      setSettingsOpen(true);
    }
  }, [isSettingsRoute]);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile, location.pathname, setOpen]);

  const menuSections = useMemo(
    () => [
      {
        key: 'general',
        title: 'General',
        items: [
          { to: '/dashboard', label: 'Dashboard', visible: canAccessMenu('dashboard', true) },
          { to: '/dashboard-sofia-edition', label: 'Sofia Dashboard', visible: canAccessMenu('dashboard', true) },
          { to: '/dashboard-sofia-edition-live', label: 'Sofia Wallboard', visible: canAccessMenu('dashboard', true) },
          { to: '/weekly-report', label: 'Weekly Report', visible: canAccessMenu('weekly report', canSeeWeeklyReport) },
          { to: '/inactive-escalation', label: 'Inactive SLA', visible: canSeeInactiveEscalationMenu },
          { to: '/mail-tracking-v2', label: 'Email Tracking', visible: canAccessMenu('mail tracking', true) },
          { to: '/simulation-results', label: 'Simulation Results', visible: canAccessMenu('simulation results', isTestAdmin) },
          { to: '/contacts', label: 'Contacts', visible: canAccessMenu('contacts', isTestAdmin) },
          { to: '/dashboardStatus', label: 'Stats', visible: canAccessMenu('stats', isAdmin) },
          { to: '/audit-v2', label: 'Одит — активност', visible: canAccessMenu('audit activity', canManageAccess || canViewAllLogs) },
          { to: '/access-audit-logs', label: 'Одит — достъп', visible: canAccessMenu('audit access', canManageAccess || canViewAllLogs) },
        ],
      },
      {
        key: 'devices',
        title: 'Devices',
        items: [
          { to: '/devices', label: 'Devices', visible: canAccessMenu('devices', isTestAdmin) },
          { to: '/pmi-devices', label: 'PMI Devices', visible: canAccessMenu('pmi devices', isTestAdmin) },
          { to: '/pmi-devicesP', label: 'PMI Devices P', visible: canAccessMenu('pmi devices p', true) },
          { to: '/my-devices', label: 'My Devices', visible: canAccessMenu('my devices', true) },
          { to: '/locations', label: 'Locations', visible: canAccessMenu('locations', isAdmin) },
          { to: '/pmi-locations', label: 'PMI Locations', visible: canAccessMenu('pmi locations', isAdmin) },
          { to: '/pmi-mapping-log', label: 'PMI Mapping Log', visible: canAccessMenu('pmi mapping log', canAccessMenu('pmi locations', isAdmin)) },
          { to: '/ta-tal-addresses', label: 'TA&TAL Addresses', visible: canAccessMenu('ta&tal addresses', isAdmin) },
          { to: '/pmi-device-logs', label: 'PMI devices logs', visible: canAccessMenu('pmi device logs', canSeePmiDeviceLogs) },
          { to: '/notify-flow-v2', label: 'Notify Flow (proto)', visible: canAccessMenu('notify flow', isTestAdmin) },
          { to: '/device-notifications-v2', label: 'Device Notifications', visible: canAccessMenu('device notifications', isTestAdmin) },
          { to: '/location-health-v2', label: 'Location Health', visible: canAccessMenu('location health', isTestAdmin) },
        ],
      },
      {
        key: 'campaigns',
        title: 'Campaigns',
        items: [
          { to: '/campaign-previews', label: 'Campaign previews', visible: canAccessMenu('campaign previews', isAdmin) },
          { to: '/campaigns', label: 'Campaign', visible: canAccessMenu('campaign', isTestAdmin) },
          { to: '/campaignsP', label: 'Campaigns', visible: canAccessMenu('campaigns', isAdmin) },
          { to: '/manual-campaign-assignment', label: 'Campaign Assignments', visible: canAccessMenu('campaign assignments', isTestAdmin) },
          { to: '/schedule', label: 'Scheduled Playback', visible: canAccessMenu('categories schedule', isTestAdmin) },
          { to: '/campaign-calendar-v2', label: 'Campaign Calendar (proto)', visible: canAccessMenu('campaign calendar', isTestAdmin) },
          { to: '/campaign-device-logs', label: 'Campaign assignment logs (legacy)', visible: canAccessMenu('campaign devices logs', isAdmin) },
          { to: '/campaign-change-logs', label: 'Campaign change logs', visible: canAccessMenu('campaign edit logs', canSeeCampaignAndFileLogs) },
        ],
      },
      {
        key: 'content',
        title: 'Content',
        items: [
          { to: '/upload-content', label: 'Upload Content', visible: canAccessMenu('upload content', isAdmin) },
          { to: '/list-files', label: 'Files', visible: canAccessMenu('files', isAdmin) },
          { to: '/products', label: 'Products', visible: canAccessMenu('products', isAdmin) },
          { to: '/imports', label: 'Imports', visible: canAccessMenu('imports', isAdmin) },
          { to: '/import', label: 'Import File', visible: canAccessMenu('import', isAdmin) },
          { to: '/categories', label: 'Categories', visible: canAccessMenu('categories', isAdmin) },
          { to: '/file-edit-logs', label: 'File edit logs', visible: canAccessMenu('file edit logs', canSeeCampaignAndFileLogs) },
          { to: '/proof-of-play', label: 'Proof of Play', visible: canAccessMenu('proof of play', true) },
          { to: '/proof-of-play-live', label: 'Proof of Play Map', visible: canAccessMenu('proof of play', true) },
        ],
      },
      {
        key: 'help',
        title: 'Help',
        items: [
          { to: '/instructions', label: 'Instructions', visible: canAccessMenu('instructions', true) },
          { to: '/instructionsNew', label: 'Инструкции', visible: canAccessMenu('instructions bg', true) },
          { to: '/executive-summary', label: 'Executive Summary', visible: canAccessMenu('executive summary', true) },
          { to: '/feedback-board', label: 'Feedback Board', visible: canAccessMenu('feedback board', true) },
          { to: '/field-instructions', label: 'Field Instructions', visible: canAccessMenu('field instructions', true) },
          { to: '/change-log', label: 'Change log', visible: canAccessMenu('change log', true) },
        ],
      },
    ],
    [canAccessMenu, canManageAccess, canSeeCampaignAndFileLogs, canSeeInactiveEscalationMenu, canSeePmiDeviceLogs, canSeeWeeklyReport, canViewAllLogs, isAdmin, isTestAdmin]
  );

  const isItemRouteActive = useCallback(
    (to) => location.pathname === to || location.pathname.startsWith(`${to}/`),
    [location.pathname]
  );

  const settingsItems = useMemo(
    () => [
      { to: '/access-control', label: 'Access Control', visible: canSeeAccessControl, sectionTitle: 'Settings' },
      { to: '/access-audit-logs', label: 'Одит — достъп', visible: canSeeAccessAuditLogs, sectionTitle: 'Settings' },
      { to: '/roles-permissions-v2', label: 'Роли и права', visible: canSeeRolesPermissions, sectionTitle: 'Settings' },
      { to: '/server-health', label: 'Server Health', visible: canSeeServerHealth, sectionTitle: 'Settings' },
      { to: '/pmi-runtime-controls', label: 'PMI Runtime Controls', visible: canSeePmiRuntimeControls, sectionTitle: 'Settings' },
      { to: '/version-management', label: 'Version Management', visible: canSeeVersionManagement, sectionTitle: 'Settings' },
      { to: '/device-configs', label: 'Device Configs', visible: canSeeDeviceConfigs, sectionTitle: 'Settings' },
    ],
    [canSeeAccessAuditLogs, canSeeAccessControl, canSeeDeviceConfigs, canSeePmiRuntimeControls, canSeeRolesPermissions, canSeeServerHealth, canSeeVersionManagement]
  );

  const visibleMenuEntries = useMemo(
    () => [
      ...menuSections.flatMap((section) =>
        section.items
          .filter((item) => item.visible)
          .map((item) => ({
            ...item,
            sectionTitle: section.title,
          }))
      ),
      ...settingsItems.filter((item) => item.visible),
    ],
    [menuSections, settingsItems]
  );
  const normalizedMenuSearch = useMemo(() => normalizeMenuSearchValue(menuSearch), [menuSearch]);
  const hasMenuSearch = normalizedMenuSearch.length > 0;

  const matchesMenuSearch = useCallback(
    (item, sectionTitle = '') => {
      if (!hasMenuSearch) return true;
      const haystack = [
        item?.label,
        item?.to,
        sectionTitle,
      ]
        .map((value) => normalizeMenuSearchValue(value))
        .filter(Boolean)
        .join(' ');
      return haystack.includes(normalizedMenuSearch);
    },
    [hasMenuSearch, normalizedMenuSearch]
  );

  const [, setRecentRoutes] = useState(() => readRecentMenuRoutes());

  const [openSections, setOpenSections] = useState(() => {
    const initialState = {};
    menuSections.forEach((section) => {
      const visibleItems = section.items.filter((item) => item.visible);
      initialState[section.key] = visibleItems.some((item) => isItemRouteActive(item.to));
    });
    return initialState;
  });

  useEffect(() => {
    setOpenSections((prev) => {
      const next = {};
      menuSections.forEach((section) => {
        const visibleItems = section.items.filter((item) => item.visible);
        const hasActiveRoute = visibleItems.some((item) => isItemRouteActive(item.to));
        next[section.key] = hasActiveRoute || prev[section.key] || false;
      });
      return next;
    });
  }, [isItemRouteActive, menuSections]);

  const filteredMenuSections = useMemo(
    () => menuSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.visible && matchesMenuSearch(item, section.title)),
      }))
      .filter((section) => section.items.length > 0),
    [matchesMenuSearch, menuSections]
  );

  const filteredSettingsItems = useMemo(
    () => settingsItems.filter((item) => item.visible && matchesMenuSearch(item, item.sectionTitle)),
    [matchesMenuSearch, settingsItems]
  );

  const hasVisibleSearchResults = filteredMenuSections.length > 0 || filteredSettingsItems.length > 0;

  useEffect(() => {
    const matchingItem = [...visibleMenuEntries]
      .sort((left, right) => right.to.length - left.to.length)
      .find((item) => isItemRouteActive(item.to));

    if (!matchingItem) {
      return;
    }

    setRecentRoutes((currentRoutes) => {
      const nextRoutes = [matchingItem.to, ...currentRoutes.filter((route) => route !== matchingItem.to)];
      writeRecentMenuRoutes(nextRoutes);
      return nextRoutes;
    });
  }, [isItemRouteActive, visibleMenuEntries]);

  const renderMenuLink = (item) => {
    // In v2 mode, hide the standalone "(v2)" duplicates — the base item resolves to them.
    if (uiVersion === 'v2' && isV2DuplicateRoute(item.to)) return null;
    const to = resolveNavRoute(item.to, uiVersion);
    const showV2Badge = uiVersion === 'v2' && hasV2Variant(item.to);
    return (
      <li key={item.to}>
        <NavLink
          to={to}
          className={({ isActive }) => (isActive ? 'active-link' : '')}
          onClick={() => {
            if (isMobile) {
              setOpen(false);
            }
          }}
        >
          {item.label}
          {showV2Badge ? (
            <span
              className="menu-v2-badge"
              style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, padding: '1px 5px', borderRadius: 999, background: '#12305c', color: '#fff', verticalAlign: 'middle' }}
            >
              v2
            </span>
          ) : null}
        </NavLink>
      </li>
    );
  };

  return (
    <Drawer
      sx={{
        width: isMobile ? 280 : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isMobile ? 280 : drawerWidth,
          boxSizing: 'border-box',
          top: 'var(--platform-header-height, 56px)',
          height: 'calc(100% - var(--platform-header-height, 56px))',
        },
      }}
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={handleDrawerClose}
      ModalProps={{ keepMounted: true }}
    >
      <DrawerHeader>
        <IconButton onClick={handleDrawerClose}>
          {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </DrawerHeader>
      <nav className="sidebar">
        <div className="sidebar-search">
          <input
            type="search"
            className="sidebar-search__input"
            value={menuSearch}
            onChange={(event) => setMenuSearch(event.target.value)}
            placeholder="Search menu"
            aria-label="Search menu"
          />
        </div>
        <ul className="sidebar-sections">
          {filteredMenuSections.map((section) => {
            const visibleItems = section.items;
            if (!visibleItems.length) return null;
            const sectionOpen = hasMenuSearch ? true : Boolean(openSections[section.key]);
            const hasActiveItem = visibleItems.some((item) => isItemRouteActive(item.to));

            return (
              <li key={section.key} className="menu-section">
                <button
                  type="button"
                  className={`menu-section-toggle ${hasActiveItem ? 'active-link' : ''}`}
                  onClick={() =>
                    setOpenSections((prev) => ({
                      ...prev,
                      [section.key]: !prev[section.key],
                    }))
                  }
                >
                  <span>{section.title}</span>
                  <span className="menu-caret">
                    {sectionOpen ? <ExpandMoreIcon fontSize="inherit" /> : <KeyboardArrowRightIcon fontSize="inherit" />}
                  </span>
                </button>
                {sectionOpen && (
                  <ul className="menu-links">
                    {visibleItems.map(renderMenuLink)}
                  </ul>
                )}
              </li>
            );
          })}

          {filteredSettingsItems.length > 0 && (
            <li className="menu-group">
              <button
                type="button"
                className={`menu-group-toggle ${isSettingsRoute ? 'active-link' : ''}`}
                onClick={() => setSettingsOpen((prev) => !prev)}
              >
                <span>Settings</span>
                <span className="menu-caret">
                  {(hasMenuSearch || settingsOpen) ? <ExpandMoreIcon fontSize="inherit" /> : <KeyboardArrowRightIcon fontSize="inherit" />}
                </span>
              </button>
              {(hasMenuSearch || settingsOpen) && (
                <ul className="submenu">
                  {filteredSettingsItems.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) => (isActive ? 'active-link' : '')}
                        onClick={() => {
                          if (isMobile) {
                            setOpen(false);
                          }
                        }}
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}

          {hasMenuSearch && !hasVisibleSearchResults ? (
            <li className="sidebar-search__empty">
              <strong>No menu matches</strong>
              <span>Try another keyword.</span>
            </li>
          ) : null}
        </ul>
      </nav>
    </Drawer>
  );
};

export default Sidebar;
